import React, { useState, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Button, CircularProgress } from "@material-ui/core";
import { Activity, Mutation, MutationPerformActionArgs, Query, QueryGetJobArgs } from "../../generated/nest-graphql";
import { PERFORM_ACTION } from "../../graphql/mutations/performAction";
import { NOTICE_DEALERSHIP_PARTS_JOB } from "../../graphql/mutations/noticeDealershipPartsJob";
import { GET_JOB } from "../../graphql/queries/getJob";
import { isDealershipJob, hasDealershipAck } from "../../lib/dealership";
import { useToggle } from "../../hooks/useToggle";
import { NuModal } from "../NuModal";
import { BaseJobActionButton } from "./BaseJobActionButton";
import Play from "@material-ui/icons/PlayCircleFilled";

export const StartJobButton: React.FC<{
  jobId: string;
}> = ({ jobId }) => {
  const [performAction, { loading }] = useMutation<Mutation, MutationPerformActionArgs>(PERFORM_ACTION);
  const [noticeDealershipParts, { loading: acking }] = useMutation<
    { noticeDealershipPartsJob: Activity },
    { jobId: string }
  >(NOTICE_DEALERSHIP_PARTS_JOB);
  const { data } = useQuery<Query, QueryGetJobArgs>(GET_JOB, {
    variables: { id: jobId },
    fetchPolicy: "cache-first",
  });
  const [disabled, toggleDisabled] = useState(true);
  const [dealershipModalIsOpen, setDealershipModalIsOpen] = useToggle();

  const job = data?.getJob;
  const needsDealershipAck = isDealershipJob(job) && !hasDealershipAck(job?.activityFeed);

  const startJob = async () => {
    await performAction({
      variables: {
        performActionInput: {
          actionName: "startJob",
          entityId: jobId,
          entityName: "Job",
        },
      },
    });
  };

  // Gate the state change: if the job has dealership parts and hasn't been
  // acknowledged yet, show the alert before starting the job.
  const handleAction = async () => {
    if (needsDealershipAck) {
      setDealershipModalIsOpen(true);
      return;
    }
    await startJob();
  };

  const handleAcknowledge = async () => {
    await noticeDealershipParts({ variables: { jobId } });
    await startJob();
    setDealershipModalIsOpen(false);
  };

  // Start button is disabled for the first 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      toggleDisabled(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [disabled, toggleDisabled]);

  return (
    <>
      <BaseJobActionButton
        disabled={disabled}
        onClick={handleAction}
        icon={<Play />}
        loading={loading}
        buttonText={`Start Job`}
      />
      <NuModal
        isOpen={dealershipModalIsOpen}
        maxWidth="sm"
        title="Dealership Parts"
        actions={
          <Button
            type={"button"}
            size={"large"}
            variant={"contained"}
            color={"primary"}
            disabled={acking || loading}
            startIcon={acking || loading ? <CircularProgress size={20} color="inherit" /> : null}
            onClick={handleAcknowledge}
          >
            {acking || loading ? "Starting…" : "Acknowledge"}
          </Button>
        }
      >
        <p>
          Job contains parts from a dealership. Please ensure you share invoice details with Technician Dispatch via
          text or Slack.
        </p>
      </NuModal>
    </>
  );
};
